import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/90",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        outline:
          "border border-input bg-background hover:bg-accent hover:text-accent-foreground",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3",
        lg: "h-11 rounded-md px-8",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, onClick, disabled, type, children, ...props }, ref) => {
    const [isLoading, setIsLoading] = React.useState(false)

    // For submit buttons: listen to the form's submit event on the button element
    // so we can show the spinner without intercepting the native submit flow.
    const buttonRef = React.useRef<HTMLButtonElement | null>(null)

    const setRefs = (el: HTMLButtonElement | null) => {
      buttonRef.current = el
      if (typeof ref === "function") ref(el)
      else if (ref) (ref as React.MutableRefObject<HTMLButtonElement | null>).current = el
    }

    // For type="submit" buttons: attach a one-time submit listener on the parent form
    // so the spinner fires on form submission without blocking the native submit.
    const handleSubmitButtonClick = React.useCallback(
      (e: React.MouseEvent<HTMLButtonElement>) => {
        if (isLoading || disabled) return
        const form = buttonRef.current?.closest("form")
        if (!form) return

        const onFormSubmit = () => {
          setIsLoading(true)
          // Auto-clear after 8s as a safety net (form navigation will unmount anyway)
          setTimeout(() => setIsLoading(false), 8000)
          form.removeEventListener("submit", onFormSubmit)
        }
        form.addEventListener("submit", onFormSubmit, { once: true })

        if (onClick) onClick(e)
      },
      [isLoading, disabled, onClick]
    )

    // For type="button" with onClick: intercept and track the async result.
    const handleButtonClick = React.useCallback(
      (e: React.MouseEvent<HTMLButtonElement>) => {
        if (isLoading || disabled) return
        if (!onClick) return

        setIsLoading(true)
        const result = onClick(e) as unknown

        if (result instanceof Promise) {
          result.finally(() => setIsLoading(false))
        } else {
          // Sync handler — clear after 5s fallback
          setTimeout(() => setIsLoading(false), 5000)
        }
      },
      [isLoading, disabled, onClick]
    )

    // asChild uses Slot — skip loading logic entirely
    if (asChild) {
      const Comp = Slot
      return (
        <Comp
          className={cn(buttonVariants({ variant, size, className }))}
          ref={ref as React.Ref<HTMLElement>}
          onClick={onClick as React.MouseEventHandler<HTMLElement>}
          {...(disabled !== undefined ? { "data-disabled": disabled } : {})}
          {...props}
        >
          {children}
        </Comp>
      )
    }

    const isSubmit = type === "submit"
    const clickHandler = isSubmit ? handleSubmitButtonClick : (onClick ? handleButtonClick : undefined)

    return (
      <button
        type={type ?? "button"}
        className={cn(
          buttonVariants({ variant, size, className }),
          "relative",
          isLoading && "brightness-75 pointer-events-none"
        )}
        ref={setRefs}
        onClick={clickHandler}
        disabled={disabled || isLoading}
        {...props}
      >
        {/* Children hidden (not removed) during loading — keeps button size stable */}
        <span className={cn("inline-flex items-center gap-2", isLoading && "opacity-0")}>
          {children}
        </span>

        {/* White spinning circle — absolutely centered over the button */}
        {isLoading && (
          <span className="absolute inset-0 flex items-center justify-center">
            <svg
              className="animate-spin h-4 w-4 text-white"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
              />
            </svg>
          </span>
        )}
      </button>
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
